import { Box, Button, CircularProgress, Skeleton, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { FileText } from "lucide-react";
import { FormField, FormSection, StepAction, StepDetail } from "../dtos";
import { fieldHiddenByRule, sectionHiddenByRule } from "../utils/conditions";
import { blockersTooltip, computeStepBlockers } from "../utils/step-blockers";
import { useStepDetail } from "../hooks/use-step-detail.hook";
import { useStepMutations } from "../hooks/use-step-mutations.hook";
import { primaryButtonSx, secondaryButtonSx } from "./field-shell";

/**
 * §Modals (decision 2026-07-30): the KYC step card matches Figma — a compact "KYC progress" panel
 * with a Preview button (frame 12107:42983 "Not started yet" state), plus the signed-document rows
 * once one is uploaded (frame 63726). The 37-field form no longer renders on the card; editing
 * moved into the KYC modal's document pane, so the modal is the single editing surface.
 */
/**
 * §48: the display/progress field set counts ONLY the fields applicable to this case's answers —
 * a conditional field gated behind a "لا" answer leaves both the form and the denominator, so a
 * client who answers No to every gate can still reach "23 of 23". Pass the data the visibility
 * should be judged against (saved submission for progress, merged draft for the editable pane).
 */
export const kycDisplayFields = (
  schema: FormSection[],
  context: Record<string, unknown> = {}
): { section: FormSection; field: FormField }[] =>
  schema.flatMap((section) =>
    sectionHiddenByRule(section, context)
      ? []
      : (section.fields ?? [])
          .filter((f) => f.l && f.t && f.t !== "document" && f.t !== "file")
          .filter((f) => !fieldHiddenByRule(f, context))
          .map((field) => ({ section, field }))
  );

export const kycFieldKey = (section: FormSection, field: FormField): string =>
  field.k ?? field.ref ?? `${section.name}.${field.l}`;

const KycCardPanel = ({
  stepUid,
  onPreview,
}: {
  stepUid: string;
  onPreview: () => void;
}) => {
  const theme = useTheme();
  const c = theme.nawy.colors.semantic;
  const { detail, isLoading } = useStepDetail({ stepUid, enabled: true });
  const mutations = useStepMutations({ stepUid });

  if (isLoading || !detail) {
    return <Skeleton variant="rounded" width="100%" height={72} />;
  }

  const schema = (detail.formSchema ?? []) as FormSection[];
  const data = (detail.submissions?.[0]?.data as Record<string, unknown>) ?? {};
  const fields = kycDisplayFields(schema, data);
  const hasValue = ({ section, field }: { section: FormSection; field: FormField }) => {
    const value = data[kycFieldKey(section, field)];
    return value !== undefined && value !== null && value !== "";
  };
  const filled = fields.filter(hasValue).length;

  // §EditsNeeded (design): name what's owed, not a bare count — the first missing SECTION plus
  // how many more ("Existing Obligations, +2 more are missing").
  const missingSections = [
    ...new Set(fields.filter((entry) => !hasValue(entry)).map(({ section }) => section.name)),
  ];
  const progress =
    filled === 0
      ? "Not started yet"
      : missingSections.length > 0
        ? missingSections.length === 1
          ? `${missingSections[0]} is missing`
          : `${missingSections[0]}, +${missingSections.length - 1} more are missing`
        : "Ready for signing";

  // §Resubmit (live bite 2026-08-05): after a reviewer sends the KYC back (rejected doc / rework),
  // the agent replaces the document IN THE MODAL — but the card had no way to send it back through
  // the flow, so the credit team never saw the fix. The step's own terminal action does exactly
  // that: completing KYC re-runs the flow and reopens Credit Review — KYC.
  const editable =
    detail.status === "IN_PROGRESS" && detail.agentAccess?.editable !== false;
  const submitAction = (detail.actions ?? []).find(
    (action: StepAction) => action.outcome === "APPROVED" && action.available !== false
  );
  const blockers = editable ? computeStepBlockers(detail as StepDetail) : [];
  const submitBlocked = blockers.length > 0;
  const submitting = mutations.complete.isLoading;

  // the signed KYC uploads recorded on the step's document placement(s)
  const signedUploads = (detail.globals ?? [])
    .filter((global) => global.active !== false && global.document?.uploads?.length)
    .flatMap((global) => global.document!.uploads!);

  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{
          // design "Slot 1" (I12107:44684;5841:109615): white ground, p md 16, radius xl 12 and a
          // 16px gap to the button — it had been drawn a size down at p 12 / radius lg / gap 12.
          p: 2,
          borderRadius: theme.nawy.radius.xl,
          backgroundColor: c.layer.primary,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 11, lineHeight: "16px", color: c.text.secondary }}>
            KYC progress
          </Typography>
          <Typography sx={{ fontSize: 14, lineHeight: "20px", color: c.text.primary }}>
            {progress}
          </Typography>
        </Box>
        {/* the design's Button is `shrink-0`: with only `whiteSpace: nowrap` from the sx helper the
            button still shrinks under its own label at 390px (the slot is ~290px wide once the
            modal, tab, paper, board, card and panel insets are paid), so the verb overflows its
            box. Pin both actions and let the text block take the squeeze instead. */}
        <Button
          variant="contained"
          disableElevation
          onClick={onPreview}
          sx={{ ...primaryButtonSx(theme), flexShrink: 0 }}
        >
          Preview
        </Button>
        {editable && submitAction && (
          <Tooltip title={submitBlocked ? blockersTooltip(blockers) : ""} arrow>
            {/* span so the tooltip still fires on the disabled button — it is the flex item here,
                so it carries the flexShrink the button's own sx cannot reach */}
            <span style={{ flexShrink: 0 }}>
              <Button
                variant="outlined"
                disabled={submitBlocked || submitting}
                startIcon={submitting ? <CircularProgress size={13} /> : undefined}
                onClick={() =>
                  mutations.complete.mutate({ actionKey: submitAction.key })
                }
                sx={secondaryButtonSx(theme)}
              >
                {submitting ? "Submitting…" : submitAction.label || "Submit"}
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      {signedUploads.map((upload) => (
        <Stack
          key={upload.uid}
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{
            p: 1.5,
            borderRadius: theme.nawy.radius.lg,
            border: `1px solid ${c.border.primary}`,
            backgroundColor: c.layer.primary,
          }}
        >
          <FileText size={18} color={c.text.secondary} />
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 14, lineHeight: "20px", color: c.text.primary }}>
              {upload.fileName}
            </Typography>
            <Typography sx={{ fontSize: 11, lineHeight: "16px", color: c.text.secondary }}>
              Uploaded
            </Typography>
          </Box>
        </Stack>
      ))}
    </Stack>
  );
};

export default KycCardPanel;
